import { useQuery } from '@tanstack/react-query';
import { UploadedFile } from './types';
import { useFileUploadActions } from './useFileUploadActions';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';

interface UseFileUploadOptions {
  autoRefreshProcessing?: boolean;
  clientId?: string;
  enabled?: boolean;
}

interface UseFileUploadResult {
  file: UploadedFile | undefined;
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  deleteFile: () => Promise<void>;
  setFileResolved: (isResolved: boolean) => Promise<void>;
  moveFile: (parentId: string | null) => Promise<void>;
  getFileStatus: () => { color: string; label: string; showLoader?: boolean };
  refetchFile: () => Promise<void>;
}

export const useFileUpload = (
  uploadedFileId: UploadedFile['id'] | undefined,
  { autoRefreshProcessing = false, clientId, enabled = true }: UseFileUploadOptions = {},
): UseFileUploadResult => {
  const { clientId: defaultClientId } = useCurrentClient();
  const selectedClientId = clientId || defaultClientId;
  const actions = useFileUploadActions();

  const {
    data: file,
    isLoading,
    isError,
    refetch,
  } = useQuery<UploadedFile>({
    queryKey: ['clients', selectedClientId, 'data-upload-files', uploadedFileId],
    enabled: !!uploadedFileId && enabled,
    refetchInterval: autoRefreshProcessing ? 5000 : false,
  });

  const refetchFile = async () => {
    await refetch();
  };

  return {
    file,
    isLoading,
    error: isError ? new Error('Failed to load file') : actions.error,
    isError,
    deleteFile: () => {
      if (!uploadedFileId) {
        return Promise.reject(new Error('File ID is required'));
      }
      return actions.deleteFile(uploadedFileId);
    },
    setFileResolved: (isResolved: boolean) =>
      file
        ? actions.setFileResolved(file, isResolved)
        : Promise.reject(new Error('File not found')),
    moveFile: (parentId: string | null) => {
      if (!file?.file) {
        return Promise.reject(new Error('File ID is required'));
      }
      return actions.moveFile(file, parentId);
    },
    getFileStatus: () =>
      file ? actions.getFileStatus(file) : { color: 'gray.6', label: 'Not Found' },
    refetchFile,
  };
};
